import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/UI/Button';
import { ActionMenuItem } from '../../components/UI/ActionMenu';
import { ResponsiveEntityList } from '../../components/UI/ResponsiveEntityList';
import { Modal } from '../../components/UI/Modal';
import {
  canRegisterInboxWebhook,
  InboxWebhookHealthBadge,
} from '../../components/Atendimento/InboxWebhookHealthBadge';
import { inboxService } from '../../services/inboxService';
import { agentBotService } from '../../services/agentBotService';
import { Inbox } from '../../types/inbox';
import { AgentBot } from '../../types/agentBot';
import { ChannelType, CHANNEL_TYPE_LABELS } from '../../types/atendimentoEnums';
import {
  InboxWebhookHealthView,
  inboxWebhookHealthFromStatus,
} from '../../types/metaWebhook';
import { useToast } from '../../components/UI/ToastProvider';
import { helpdeskRoutes } from '../../routes/helpdeskRoutes';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

export const InboxesListPage: React.FC = () => {
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [bots, setBots] = useState<AgentBot[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [inboxToDelete, setInboxToDelete] = useState<Inbox | null>(null);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [inboxToRegister, setInboxToRegister] = useState<Inbox | null>(null);
  const [registering, setRegistering] = useState(false);
  const [healthById, setHealthById] = useState<Record<string, InboxWebhookHealthView>>({});
  const fetchedHealthIds = useRef(new Set<string>());
  const inFlightHealthIds = useRef(new Set<string>());
  const { addToast } = useToast();

  const fetchInboxes = async () => {
    setLoading(true);
    try
    {
      const [inboxesData, botsData] = await Promise.all([
        inboxService.getAll(),
        agentBotService.getAll(),
      ]);
      setInboxes(inboxesData);
      setBots(botsData);
    }
    catch
    {
      addToast('Erro ao carregar caixas de entrada', 'error');
    }
    finally
    {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchInboxes();
  }, []);

  useEffect(() => {
    const whatsappInboxes = inboxes.filter(inbox => inbox.channelType === ChannelType.Whatsapp);
    const pending = whatsappInboxes.filter(inbox =>
      !fetchedHealthIds.current.has(inbox.id) && !inFlightHealthIds.current.has(inbox.id)
    );
    if (pending.length === 0)
    {
      return;
    }

    pending.forEach(inbox => inFlightHealthIds.current.add(inbox.id));
    setHealthById(prev => {
      const next = { ...prev };
      pending.forEach(inbox => {
        next[inbox.id] = { status: 'loading' };
      });
      return next;
    });

    let cancelled = false;

    const loadHealth = async () => {
      await Promise.all(pending.map(async inbox => {
        try
        {
          const data = await inboxService.getMetaWebhookHealth(inbox.id);
          if (cancelled)
          {
            return;
          }

          fetchedHealthIds.current.add(inbox.id);
          setHealthById(prev => ({
            ...prev,
            [inbox.id]: inboxWebhookHealthFromStatus(data),
          }));
        }
        catch (error)
        {
          if (cancelled)
          {
            return;
          }

          fetchedHealthIds.current.add(inbox.id);
          setHealthById(prev => ({
            ...prev,
            [inbox.id]: {
              status: 'error',
              message: apiErrorMessage(error, 'Não foi possível consultar o webhook na Meta.'),
            },
          }));
        }
        finally
        {
          inFlightHealthIds.current.delete(inbox.id);
        }
      }));
    };

    void loadHealth();

    return () => {
      cancelled = true;
      pending.forEach(inbox => inFlightHealthIds.current.delete(inbox.id));
    };
  }, [inboxes]);

  const filtered = inboxes.filter(i => i.name.toLowerCase().includes(search.toLowerCase()));

  const getBotName = (agentBotId?: string | null) => {
    if (!agentBotId)
    {
      return null;
    }

    return bots.find(bot => bot.id === agentBotId)?.name ?? null;
  };

  const handleDelete = async () => {
    if (!inboxToDelete)
    {
      return;
    }

    try
    {
      await inboxService.delete(inboxToDelete.id);
      fetchedHealthIds.current.delete(inboxToDelete.id);
      addToast('Caixa de entrada excluída', 'success');
      setIsDeleteModalOpen(false);
      setInboxToDelete(null);
      await fetchInboxes();
    }
    catch
    {
      addToast('Erro ao excluir caixa de entrada', 'error');
    }
  };

  const openRegisterModal = (inbox: Inbox) => {
    setInboxToRegister(inbox);
    setIsRegisterModalOpen(true);
  };

  const handleRegisterWebhook = async () => {
    if (!inboxToRegister)
    {
      return;
    }

    setRegistering(true);
    try
    {
      const result = await inboxService.registerMetaWebhook(inboxToRegister.id);
      const healthStatus = result.healthStatus;
      if (healthStatus)
      {
        setHealthById(prev => ({
          ...prev,
          [inboxToRegister.id]: inboxWebhookHealthFromStatus(healthStatus),
        }));
      }
      else if (result.success)
      {
        const data = await inboxService.getMetaWebhookHealth(inboxToRegister.id);
        setHealthById(prev => ({
          ...prev,
          [inboxToRegister.id]: inboxWebhookHealthFromStatus(data),
        }));
      }

      if (result.success)
      {
        addToast(result.message || 'Webhook registrado na Meta', 'success');
      }
      else
      {
        addToast(result.error || result.message || 'Falha ao registrar o webhook na Meta.', 'error');
      }

      setIsRegisterModalOpen(false);
      setInboxToRegister(null);
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Falha ao registrar o webhook na Meta.'), 'error');
    }
    finally
    {
      setRegistering(false);
    }
  };

  const buildActionItems = (inbox: Inbox): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      {
        key: 'edit',
        label: 'Editar',
        to: helpdeskRoutes.settingsInboxEdit(inbox.id),
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        ),
      },
    ];

    if (canRegisterInboxWebhook(healthById[inbox.id]))
    {
      items.push({
        key: 'register-webhook',
        label: 'Finalizar configuração',
        onClick: () => openRegisterModal(inbox),
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ),
      });
    }

    items.push({
      key: 'delete',
      label: 'Excluir',
      variant: 'danger',
      onClick: () => {
        setInboxToDelete(inbox);
        setIsDeleteModalOpen(true);
      },
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      ),
    });

    return items;
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Caixas de Entrada</h1>
          <p className="text-gray-600 text-sm mt-1">Configure canais omnichannel</p>
        </div>
        <Link to={helpdeskRoutes.settingsInboxNew}>
          <Button variant="primary">Nova caixa</Button>
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <input
          type="text"
          placeholder="Buscar caixas..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 mb-4">Nenhuma caixa de entrada configurada.</p>
          <Link to={helpdeskRoutes.settingsInboxNew}>
            <Button variant="primary">Criar primeira caixa</Button>
          </Link>
        </div>
      ) : (
        <ResponsiveEntityList
          items={filtered}
          getKey={(inbox) => inbox.id}
          mobileTitle={(inbox) => inbox.name}
          mobileTo={(inbox) => helpdeskRoutes.settingsInboxEdit(inbox.id)}
          mobileFields={(inbox) => [
            {
              label: 'Canal',
              value: (
                <span className="inline-flex items-center gap-2">
                  {CHANNEL_TYPE_LABELS[inbox.channelType]}
                  {inbox.channelType === ChannelType.Whatsapp && (
                    <InboxWebhookHealthBadge health={healthById[inbox.id]} />
                  )}
                </span>
              ),
            },
            { label: 'Membros', value: String(inbox.memberUserIds.length) },
            { label: 'Bot', value: getBotName(inbox.agentBotId) ?? '—' },
            {
              label: 'Status',
              value: (
                <span className={`inline-flex px-2 py-1 text-xs rounded-full ${inbox.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                  {inbox.isActive ? 'Ativa' : 'Inativa'}
                </span>
              ),
            },
          ]}
          columns={[
            {
              header: 'Nome',
              cellClassName: 'px-6 py-4 text-sm font-medium text-gray-900',
              cell: (inbox) => inbox.name,
            },
            {
              header: 'Canal',
              cell: (inbox) => (
                <span className="inline-flex items-center gap-2">
                  {CHANNEL_TYPE_LABELS[inbox.channelType]}
                  {inbox.channelType === ChannelType.Whatsapp && (
                    <InboxWebhookHealthBadge health={healthById[inbox.id]} />
                  )}
                </span>
              ),
            },
            {
              header: 'Membros',
              cell: (inbox) => inbox.memberUserIds.length,
            },
            {
              header: 'Bot',
              cell: (inbox) => getBotName(inbox.agentBotId) ?? '—',
            },
            {
              header: 'Status',
              cell: (inbox) => (
                <span className={`inline-flex px-2 py-1 text-xs rounded-full ${inbox.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                  {inbox.isActive ? 'Ativa' : 'Inativa'}
                </span>
              ),
            },
          ]}
          actions={(inbox) => buildActionItems(inbox)}
        />
      )}

      <Modal isOpen={isDeleteModalOpen} title="Excluir caixa de entrada" onClose={() => setIsDeleteModalOpen(false)}>
        <p className="text-gray-600 mb-4">
          Deseja excluir a caixa <strong>{inboxToDelete?.name}</strong>? As conversas desta caixa também serão removidas.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleDelete}>Excluir</Button>
        </div>
      </Modal>

      <Modal
        isOpen={isRegisterModalOpen}
        title="Finalizar configuração"
        onClose={() => {
          if (!registering)
          {
            setIsRegisterModalOpen(false);
            setInboxToRegister(null);
          }
        }}
      >
        <p className="text-gray-600 mb-4">
          Finalizar a configuração da caixa <strong>{inboxToRegister?.name}</strong>?
        </p>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            disabled={registering}
            onClick={() => {
              setIsRegisterModalOpen(false);
              setInboxToRegister(null);
            }}
          >
            Cancelar
          </Button>
          <Button variant="primary" disabled={registering} onClick={() => void handleRegisterWebhook()}>
            {registering ? 'Configurando...' : 'Finalizar configuração'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};
